import classNames from 'classnames'
import { DropDown, DropDownOption } from 'components/styled/index'
import DrupalContext from 'contexts/DrupalContext'
import { FetchContext } from 'contexts/FetchContext'
import useSuggestions from 'hooks/useSuggestions'
import useUrlParams from 'hooks/useUrlParams'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

function SearchBox() {
  const drupal = useContext(DrupalContext)
  const { t } = useTranslation()
  const { urlParams, updateUrlParams } = useUrlParams()
  const inputBox = useRef(null)
  const formItem = useRef(null)
  const [userInput, setUserInput] = useState('')
  const [open, setOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [suggestionQuery, setSuggestionQuery] = useState(null)
  const { suggestions } = useSuggestions(suggestionQuery)
  const { setIsAppendResults } = useContext(FetchContext)

  useEffect(() => {
    inputBox?.current?.focus()
    setSuggestionQuery(urlParams.q)
  }, [])

  useEffect(() => {
    setUserInput(urlParams.q)
  }, [urlParams.q])

  useEffect(() => {
    setSuggestionQuery(null)
  }, [urlParams.searcher])

  useEffect(() => {
    setSelectedIndex(-1)
  }, [suggestions])

  useEffect(() => {
    // Add event listener when mounted.
    document.addEventListener('mousedown', handleClick)
    // Remove the event listener when unmounted.
    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [])

  const handleClick = (e) => {
    if (!formItem.current.contains(e.target)) {
      setOpen(false)
    }
  }

  const handleInput = (value) => {
    // Never append, since it should always result in new query
    setIsAppendResults(false)
    setUserInput(value)
    setSuggestionQuery(value)
    setOpen(true)
  }

  const handleSearch = (query) => {
    // @todo Temporary fix for main search widget. This is to be replaced with
    // permanent solution ASAP.
    // If component is displayed as a widget, redirect user to search page
    // instead of using pushState.
    if (drupal.type === 'widget') {
      window.location = `${drupal.path}?q=${query}`
    } else {
      updateUrlParams({ hits: undefined, q: query || undefined }, drupal.path)
      setSuggestionQuery(query)
      setOpen(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && selectedIndex === -1) {
      return handleSearch(userInput)
    }

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        setOpen(true)
        e.preventDefault()
    }

    if (open) {
      switch (e.key) {
        case 'ArrowDown':
          return setSelectedIndex(
            selectedIndex >= suggestions.length - 1 ? 0 : selectedIndex + 1
          )
        case 'ArrowUp':
          return setSelectedIndex(
            selectedIndex <= 0 ? suggestions.length - 1 : selectedIndex - 1
          )
        case 'Enter':
          return handleSearch(suggestions[selectedIndex]?.text)
        case 'Esc': // IE/Edge specific value
        case 'Escape':
        case 'Tab':
          return setOpen(false)
      }
    }
  }

  return (
    <div className="form u-flex u-max-width-full">
      <div
        className="form-item u-max-width-full"
        ref={formItem}
        // onClick={() => handleClick(e)}
      >
        <div className="form-item__icon">
          <svg
            role="presentation"
            aria-hidden="true"
            className="icon icon--search"
          >
            <use xlinkHref="#icon-search" />
          </svg>
        </div>
        <label className="visually-hidden" htmlFor={`${drupal.id}-input`}>
          {t('Search')}
        </label>
        <input
          type="text"
          role="search"
          autoComplete="off"
          className="form-item__input form-item__input--text form-item__input--white form-item__input--site-search"
          placeholder={t('Enter query')}
          id={`${drupal.id}-input`}
          ref={inputBox}
          value={userInput}
          onClick={() => handleInput(urlParams.q)}
          onChange={(e) => handleInput(e.target.value)}
          onKeyDown={(e) => handleKeyDown(e)}
        />

        {!!open && !!suggestions.length && (
          <DropDown>
            {suggestions.map(({ label, text }, index) => (
              <DropDownOption
                tabIndex={-1}
                className={classNames('option', {
                  active: index === selectedIndex,
                })}
                onClick={() => handleSearch(text)}
                dangerouslySetInnerHTML={{ __html: label }}
              />
            ))}
          </DropDown>
        )}
      </div>

      <button
        className="button button--primary button--small u-ml-100 u-ml-150-large-up"
        id={`${drupal.id}-submit`}
        onClick={() => handleSearch(userInput || '*')}
      >
        {t('Search')}
      </button>
    </div>
  )
}

export default SearchBox
